import { FiAlertTriangle, FiX } from 'react-icons/fi';
import { cn } from '@/lib/utils';

interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning';
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  children?: React.ReactNode;
}

export default function ConfirmModal({ isOpen, title, message, confirmText = 'Confirm', cancelText = 'Cancel', variant, loading, onConfirm, onCancel, children }: ConfirmModalProps) {
  if (!isOpen) return null;

  const isDanger = variant === 'danger';

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-[1000]"
      onClick={onCancel}
    >
      <div
        className="bg-background-secondary border border-border rounded-xl w-full max-w-[420px] p-6 shadow-[0_8px_32px_rgba(0,0,0,0.4)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-4 mb-4">
          <div
            className={cn(
              'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0',
              isDanger ? 'bg-danger-bg text-danger' : 'bg-warning-bg text-warning'
            )}
          >
            <FiAlertTriangle size={20} />
          </div>
          <div className="flex-1">
            <h3 className="m-0 mb-2 text-lg font-semibold text-text-primary">{title}</h3>
            <p className="m-0 text-sm text-text-secondary leading-relaxed">{message}</p>
          </div>
          <button
            onClick={onCancel}
            className="bg-transparent border-none text-text-secondary cursor-pointer p-1 rounded hover:text-text-primary hover:bg-background-hover"
          >
            <FiX size={18} />
          </button>
        </div>

        {children}

        <div className="flex gap-3 mt-6 justify-end">
          <button
            onClick={onCancel}
            disabled={loading}
            className="py-2.5 px-5 rounded-lg text-sm font-medium cursor-pointer transition-all duration-200 bg-background-hover text-text-primary border border-border-light hover:bg-background-active disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={cn(
              'py-2.5 px-5 rounded-lg text-sm font-medium cursor-pointer transition-all duration-200 border-none disabled:opacity-50 disabled:cursor-not-allowed',
              isDanger
                ? 'bg-danger text-white hover:bg-danger-hover'
                : 'bg-accent text-white hover:bg-accent-hover'
            )}
          >
            {loading ? 'Please wait...' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
